<div class="space-y-6">
  <!-- Header -->
  <div class="flex items-center justify-between">
    <h1 class="text-2xl font-bold text-gray-900">Patient Management</h1>
    <app-button variant="primary" (clicked)="openAddModal()">
      <svg class="w-5 h-5 inline mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
      </svg>
      Add Patient
    </app-button>
  </div>

  <!-- Search Bar -->
  <div class="card">
    <app-input
      type="text"
      [ngModel]="searchQuery()"
      (ngModelChange)="onSearchChange($event)"
      placeholder="Search patients by name, email, or phone...">
    </app-input>
  </div>

  <!-- Patients Table -->
  <div class="card overflow-hidden">
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date of Birth</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gender</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Insurance</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          @if (filteredPatients().length === 0) {
            <tr>
              <td colspan="6" class="px-6 py-8 text-center text-gray-500">No patients found</td>
            </tr>
          } @else {
            @for (patient of filteredPatients(); track patient.id) {
              <tr class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold mr-3">
                      {{ patient.firstName.charAt(0) }}{{ patient.lastName.charAt(0) }}
                    </div>
                    <div>
                      <div class="text-sm font-medium text-gray-900">{{ patient.firstName }} {{ patient.lastName }}</div>
                      <div class="text-sm text-gray-500">{{ patient.email }}</div>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="text-sm text-gray-900">{{ patient.phone }}</div>
                  <div class="text-sm text-gray-500">{{ patient.city }}, {{ patient.state }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ patient.dateOfBirth | date:'shortDate' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ patient.gender }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  @if (patient.insurance) {
                    <div class="text-sm text-gray-900">{{ patient.insurance.provider }}</div>
                    <div class="text-sm text-gray-500">{{ patient.insurance.policyNumber }}</div>
                  } @else {
                    <span class="text-sm text-gray-400">No insurance</span>
                  }
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <a [routerLink]="['/patient-console', patient.id]"
                     class="text-green-600 hover:text-green-900 mr-4 font-medium">Open Console</a>
                  <button (click)="openEditModal(patient)" class="text-primary-600 hover:text-primary-900 mr-4">Edit</button>
                  <app-button variant="danger" (clicked)="deletePatient(patient.id)">Delete</app-button>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
  </div>

  <!-- Patient Modal -->
  @if (showModal()) {
    <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" (click)="closeModal()">
      <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" (click)="$event.stopPropagation()">
        <div class="p-6 border-b">
          <h2 class="text-xl font-semibold text-gray-900">
            {{ editingPatient() ? 'Edit Patient' : 'Add New Patient' }}
          </h2>
        </div>
        <div class="p-6 space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-input
                label="First Name"
                type="text"
                [(ngModel)]="patientForm().firstName">
              </app-input>
            </div>
            <div>
              <app-input
                label="Last Name"
                type="text"
                [(ngModel)]="patientForm().lastName">
              </app-input>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-input
                label="Email"
                type="email"
                [(ngModel)]="patientForm().email">
              </app-input>
            </div>
            <div>
              <app-input
                label="Phone"
                type="tel"
                [(ngModel)]="patientForm().phone">
              </app-input>
            </div>
          </div>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <app-date
                label="Date of Birth"
                [(ngModel)]="patientForm().dateOfBirth"
                [required]="true"
                name="dateOfBirth"
                (ngModelChange)="onDateChange($event)">
              </app-date>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select [(ngModel)]="patientForm().gender" class="input-field">
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          <div>
            <app-input
              label="Address"
              type="text"
              [(ngModel)]="patientForm().address">
            </app-input>
          </div>
          <div class="grid grid-cols-3 gap-4">
            <div>
              <app-input
                label="City"
                type="text"
                [(ngModel)]="patientForm().city">
              </app-input>
            </div>
            <div>
              <app-input
                label="State"
                type="text"
                [(ngModel)]="patientForm().state">
              </app-input>
            </div>
            <div>
              <app-input
                label="Zip Code"
                type="text"
                [(ngModel)]="patientForm().zipCode">
              </app-input>
            </div>
          </div>
          <div class="border-t pt-4">
            <h3 class="text-lg font-medium text-gray-900 mb-3">Emergency Contact</h3>
            <div class="grid grid-cols-3 gap-4">
              <div>
                <app-input
                  label="Name"
                  type="text"
                  [(ngModel)]="patientForm().emergencyContact!.name">
                </app-input>
              </div>
              <div>
                <app-input
                  label="Phone"
                  type="tel"
                  [(ngModel)]="patientForm().emergencyContact!.phone">
                </app-input>
              </div>
              <div>
                <app-input
                  label="Relationship"
                  type="text"
                  [(ngModel)]="patientForm().emergencyContact!.relationship">
                </app-input>
              </div>
            </div>
          </div>
        </div>
        <div class="p-6 border-t flex justify-end space-x-3">
          <app-button variant="secondary" (clicked)="closeModal()">Cancel</app-button>
          <app-button variant="primary" (clicked)="savePatient()">Save</app-button>
        </div>
      </div>
    </div>
  }
</div>

